import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import { unreadCountAtom } from '../atoms/notificationAtoms';
import { fetchNotifications, markNotificationsRead, fetchUnreadCount } from '../api/notifications';
import { formatTimeAgo } from '../helpers/timeHelper';

const DROPDOWN_LIMIT = 10;

function NotificationBell() {
    const [unreadCount, setUnreadCount] = useAtom(unreadCountAtom);
    const [open, setOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const bellRef = useRef(null);
    const location = useLocation();

    // Re-fetch unread count on route change
    useEffect(() => {
        let cancelled = false;
        fetchUnreadCount().then(count => {
            if (!cancelled) setUnreadCount(count);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [location.pathname, setUnreadCount]);

    // Close dropdown on route change
    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (bellRef.current && !bellRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleToggle = useCallback(async () => {
        const willOpen = !open;
        setOpen(willOpen);

        if (!willOpen) return;

        setLoading(true);
        try {
            const data = await fetchNotifications({ limit: DROPDOWN_LIMIT });
            setNotifications(data.notifications);

            // Mark unread ones as read
            const unreadIDs = data.notifications
                .filter(n => !n.readAt)
                .map(n => n.notificationID);
            if (unreadIDs.length > 0) {
                await markNotificationsRead(unreadIDs);
                const now = new Date().toISOString();
                setNotifications((prev) =>
                    prev.map((n) => (unreadIDs.includes(n.notificationID) ? { ...n, readAt: n.readAt ?? now } : n))
                );
                setUnreadCount((prev) => Math.max(0, (prev ?? 0) - unreadIDs.length));
            }
        } catch {
            // silent — bell is best-effort UI
        } finally {
            setLoading(false);
        }
    }, [open, setUnreadCount]);

    return (
        <div className="notification-bell-container" ref={bellRef}>
            <button
                className="nav-icon notification-bell-trigger"
                onClick={handleToggle}
                title="Notifications"
            >
                <FontAwesomeIcon icon={faBell} />
                {unreadCount > 0 ? <span className="nav-badge">{unreadCount}</span> : null}
            </button>

            {open ? (
                <div className="notification-dropdown">
                    <div className="notification-dropdown-header">
                        <span>Notifications</span>
                    </div>
                    <div className="notification-dropdown-list">
                        {loading ? (
                            <div className="notification-loading">Loading...</div>
                        ) : notifications.length === 0 ? (
                            <div className="notification-empty">No notifications yet</div>
                        ) : (
                            notifications.map(n => (
                                <div key={n.notificationID} className={`notification-item ${n.readAt ? 'read' : 'unread'}`}>
                                    {n.link ? (
                                        <Link to={n.link} className="notification-link" onClick={() => setOpen(false)}>
                                            <span className="notification-message">{n.message}</span>
                                            <span className="notification-time">{formatTimeAgo(n.created)}</span>
                                        </Link>
                                    ) : (
                                        <div className="notification-link">
                                            <span className="notification-message">{n.message}</span>
                                            <span className="notification-time">{formatTimeAgo(n.created)}</span>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                    <Link to="/notifications" className="notification-view-all" onClick={() => setOpen(false)}>
                        View all notifications
                    </Link>
                </div>
            ) : null}
        </div>
    );
}

export default NotificationBell;
